import { useLazyQuery } from "@apollo/client";
import { ClockIcon, MoonIcon } from "@heroicons/react/24/solid";
import CenterFocusWeakIcon from '@mui/icons-material/CenterFocusWeak';
import { ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { Map, useMap } from "@vis.gl/react-google-maps";
import { addMinutes } from "date-fns";
import { useEffect, useState } from "react";
import styled from "styled-components";
import FilterChip from "../../components/FilterChip/FilterChip";
import Page from "../../components/Page/Page";
import {
  MapChip,
  MapContainer,
  MapControls,
  MapHeadline,
} from "../../components/Styled/MapChrome";
import {
  UserActivityFilter,
  UserActivityMapResult,
} from "../../graphql/graphql";
import { USER_ACTIVITY_MAP } from "../../graphql/queries-gql";
import { timeSinceString } from "../../util/helpers";
import ClusteredPoiMarkers, { Poi } from "./ClusteredPoiMarkers";
import NightOverlay from "./NightOverlay";
import TimeZoneMarkers from "./TimeZoneMarkers";

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const FilterDivider = styled.span`
  width: 1px;
  align-self: stretch;
  margin: 4px 4px;
  background: ${(props) => props.theme.border};
`;

// https://developers.google.com/codelabs/maps-platform/maps-platform-101-react-js#0

const UserActivityMap = () => {
  const [minutes, setMinutes] = useState<number>(30);
  const [filters, setFilters] = useState<UserActivityFilter[]>([]);
  const [includeSignups, setIncludeSignups] = useState<boolean>(false);
  const [includeSignupReady, setIncludeSignupReady] = useState<boolean>(false);
  const [showNightOverlay, setShowNightOverlay] = useState<boolean>(true);
  const [showTimeZones, setShowTimeZones] = useState<boolean>(true);
  const [pois, setPois] = useState<Poi[]>([]);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [fetchUserActivityMap, { data, loading, error }] = useLazyQuery<{
    userActivityMap: UserActivityMapResult;
  }>(USER_ACTIVITY_MAP, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      // console.log(
      //   "fetchUserActivityMap onCompleted",
      //   JSON.stringify(data, null, 2)
      // );
      const total = data.userActivityMap.data.length;

      const newPois = data.userActivityMap.data
        .filter((activity) =>
          activity.lat != null &&
          activity.lng != null &&
          !(activity.lat === 0 && activity.lng === 0)
        )
        .map((activity) => {
          return {
            key: activity.userId,
            location: {
              lat: activity.lat,
              lng: activity.lng,
            },
            userActivityMapData: activity,
          };
        });

      setTotalUsers(total);
      setPois(newPois);
    },
    onError: (error) => {
      console.log("fetchUserActivityMap onError", error);
    },
  });
  const map = useMap();

  // Fetch data when filters change and set up interval
  useEffect(() => {
    // Immediate fetch
    fetchUserActivityMap({
      variables: {
        args: {
          minutes,
          filters,
          includeSignups,
          includeSignupReady,
        },
      },
    });

    // Set up interval for periodic refetch
    const timer = minutes <= 30 ? 30000 : 1000 * 60 * 5;

    const interval = setInterval(() => {
      fetchUserActivityMap({
        variables: {
          args: {
            minutes,
          filters,
            includeSignups,
            includeSignupReady,
          },
        },
      });
    }, timer);

    return () => {
      clearInterval(interval);
    };
  }, [minutes, filters, includeSignups, includeSignupReady, fetchUserActivityMap]);

  const isChecked = (filter: UserActivityFilter): boolean => {
    return filters.includes(filter);
  };
  const handleCheck = (filter: UserActivityFilter, checked: boolean) => {
    let newFilters = filters.filter((f) => f !== filter);
    if (checked) {
      newFilters.push(filter);
    }

    // Some filters should not be allowed to be combined
    if (filter === UserActivityFilter.NewSignups) {
      newFilters = newFilters.filter(
        (f) => f !== UserActivityFilter.OldSignups
      );
    }
    if (filter === UserActivityFilter.OldSignups) {
      newFilters = newFilters.filter(
        (f) => f !== UserActivityFilter.NewSignups
      );
    }
    if (filter === UserActivityFilter.Men) {
      newFilters = newFilters.filter((f) => f !== UserActivityFilter.Women);
    }
    if (filter === UserActivityFilter.Women) {
      newFilters = newFilters.filter((f) => f !== UserActivityFilter.Men);
    }
    if (filter === UserActivityFilter.Ios) {
      newFilters = newFilters.filter((f) => f !== UserActivityFilter.Android);
    }
    if (filter === UserActivityFilter.Android) {
      newFilters = newFilters.filter((f) => f !== UserActivityFilter.Ios);
    }

    setFilters(newFilters);
  };

  const filterChip = (label: string, filter: UserActivityFilter) => (
    <FilterChip
      label={label}
      selected={isChecked(filter)}
      onChange={(checked) => handleCheck(filter, checked)}
    />
  );

  return (
    <Page
      fill
      title="Activity map"
      actions={
        <ToggleButtonGroup
          value={minutes}
          exclusive
          size="small"
          onChange={(_, newValue) => {
            if (newValue !== null) {
              setMinutes(newValue);
            }
          }}
        >
          <ToggleButton value={5}>5m</ToggleButton>
          <ToggleButton value={15}>15m</ToggleButton>
          <ToggleButton value={30}>30m</ToggleButton>
          <ToggleButton value={60}>1h</ToggleButton>
          <ToggleButton value={180}>3h</ToggleButton>
          <ToggleButton value={480}>8h</ToggleButton>
          <ToggleButton value={1440}>24h</ToggleButton>
        </ToggleButtonGroup>
      }
      toolbar={
        <>
          <MapHeadline style={{ marginRight: "auto" }}>
            <strong>{totalUsers.toLocaleString()}</strong>
            <span>
              users active in last{" "}
              {timeSinceString(addMinutes(new Date(), -minutes))}
              {totalUsers > pois.length &&
                ` (${totalUsers - pois.length} missing location)`}
            </span>
          </MapHeadline>
          <FilterRow>
            {filterChip("Premium", UserActivityFilter.Premium)}
            {filterChip("AdFree", UserActivityFilter.AdFree)}
            <FilterDivider />
            {filterChip("New users", UserActivityFilter.NewSignups)}
            {filterChip("Old users", UserActivityFilter.OldSignups)}
            <FilterDivider />
            {filterChip("Men", UserActivityFilter.Men)}
            {filterChip("Women", UserActivityFilter.Women)}
            <FilterDivider />
            {filterChip("iOS", UserActivityFilter.Ios)}
            {filterChip("Android", UserActivityFilter.Android)}
            <FilterDivider />
            <FilterChip
              label="Signups"
              selected={includeSignups}
              onChange={setIncludeSignups}
            />
            <FilterChip
              label="Signup finished"
              selected={includeSignupReady}
              onChange={setIncludeSignupReady}
            />
          </FilterRow>
        </>
      }
    >
      <MapContainer>
        <MapControls>
          <MapChip as="div">
            {pois.length.toLocaleString()} on map{" "}
            {totalUsers > 0 &&
              `(${Math.round((pois.length / totalUsers) * 100)}%)`}
          </MapChip>
          <MapChip
            $active={showTimeZones}
            onClick={() => setShowTimeZones((v) => !v)}
            title={showTimeZones ? "Hide time zones" : "Show time zones"}
          >
            <ClockIcon />
            {showTimeZones ? "Time" : "Time off"}
          </MapChip>
          <MapChip
            $active={showNightOverlay}
            onClick={() => setShowNightOverlay((v) => !v)}
            title={showNightOverlay ? "Hide night overlay" : "Show night overlay"}
          >
            <MoonIcon />
            {showNightOverlay ? "Night" : "Night off"}
          </MapChip>
          <Tooltip title="Reset map view">
            <MapChip
              aria-label="Reset map view"
              onClick={() => {
                map?.setZoom(3.1);
                map?.setCenter({
                  lat: 35.613266732528366,
                  lng: 9.880013000000014,
                });
              }}
            >
              <CenterFocusWeakIcon style={{ width: 18, height: 18 }} />
            </MapChip>
          </Tooltip>
        </MapControls>
        <Map
          mapId="2465c920699db95c"
          style={{ width: "100%", height: "100%" }}
          defaultZoom={3.1}
          defaultCenter={{ lat: 35.613266732528366, lng: 9.880013000000014 }}
          onCameraChanged={() => {}}
          onZoomChanged={() => {}}
        >
          {pois && <ClusteredPoiMarkers pois={pois} />}
          <NightOverlay enabled={showNightOverlay} />
          <TimeZoneMarkers enabled={showTimeZones} />
        </Map>
      </MapContainer>
    </Page>
  );
};

export default UserActivityMap;
